import React from "react";
import { MONTHS } from "../config";

function Terms() {
    const now = new Date();
    const last_update = MONTHS[now.getMonth()] + ' ' + now.getFullYear();
    return (
        <div className="pt-24 pb-16 text-[#1a1a1a] min-h-screen">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-block mb-6">
                        <div className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white mx-auto rounded-full shadow-lg shadow-white/50"></div>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#C8102E] to-[#A00D25] bg-clip-text text-transparent drop-shadow-lg">
                        Terms of Service
                    </h1>
                    <p className="text-[#2d1810] text-lg">Last updated: {last_update}</p>
                </div>

                {/* Introduction */}
                <div className="bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-2xl p-6 md:p-8 mb-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h2 className="text-2xl font-semibold mb-4 text-white">General Terms and Conditions of Use</h2>
                    <p className="text-white/90 leading-relaxed">
                        Using any application developed by KidKatGames constitutes full acceptance of these Terms and Conditions.
                        Our goal is to provide a safe, healthy, and responsible environment for all users. Please read the following
                        terms carefully before using the applications.
                    </p>
                </div>

                {/* Section 1 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-white text-[#C8102E] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                        Membership and Usage Conditions
                    </h3>
                    <div className="space-y-4 text-white/90">
                        <p className="leading-relaxed">
                            Membership is permitted for users aged <span className="font-semibold text-white">13 and above</span>.
                            Users under 13 may register only with the consent of a legal guardian.
                        </p>
                        <p className="leading-relaxed">
                            For applications requiring registration, users must provide <span className="font-semibold text-white">accurate, up-to-date, and real information</span>.
                        </p>
                        <p className="leading-relaxed">
                            Creating fake, duplicate, or impersonated accounts is <span className="font-semibold text-red-300">strictly prohibited</span>.
                        </p>
                        <p className="leading-relaxed">
                            Users are responsible for protecting their account information (passwords, etc.).
                        </p>
                    </div>
                </section>

                {/* Section 2 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-white text-[#C8102E] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                        User Responsibilities and Conduct
                    </h3>
                    <p className="mb-4 text-white/90 leading-relaxed">
                        Users must refrain from posting, sharing, or sending any content that includes:
                    </p>
                    <ul className="space-y-3 text-white/90">
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Immoral, obscene, violent, or offensive material</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Insults, slander, threats, or mockery of others</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Unauthorized advertisements or spam</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Rumors, false news, or misleading information</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Racist, ethnic, or provocative content</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Content violating national laws or others' intellectual property rights</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Anti-religious or anti-government material</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Threats, intimidation, or impersonation of others</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Altering, simulating, or hacking the product</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Storing or damaging in-game content such as profiles or images</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Copying, modifying, translating, or selling any game content developed by the developer</span>
                        </li>
                    </ul>
                    <p className="mt-6 text-white/90 leading-relaxed font-semibold">
                        Any misuse of the game's features to deceive, threaten, or harass others is strictly prohibited.
                    </p>
                </section>

                {/* Section 3 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-white text-[#C8102E] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                        Rules for Social and Hidden-Role Games
                    </h3>
                    <ul className="space-y-3 text-white/90">
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Inappropriate usernames may result in <span className="font-semibold text-white">permanent account suspension</span>.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Any insult or disrespect toward other players is considered misconduct.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Disrespect toward any ethnicity or religion results in a <span className="font-semibold text-red-300">permanent ban</span>.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>These games are intended for users aged <span className="font-semibold text-white">18 and above</span>; underage use is not permitted.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Political discussions are strictly forbidden and will result in penalties.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>All game images and sounds are the property of KidKat Studio; unauthorized use is subject to legal action.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Swearing or making others swear is a violation.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Declaring or assigning roles to oneself or others is prohibited and punishable.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Each person may only have one account.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Phone numbers are used solely for identification, login, and preventing duplicate accounts.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Collusion or cheating in the game will lead to account suspension.</span>
                        </li>
                    </ul>
                </section>

                {/* Section 4 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-white text-[#C8102E] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                        User Communication and Interaction
                    </h3>
                    <ul className="space-y-3 text-white/90">
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Messaging must always be respectful and comply with national laws.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Sending inappropriate messages, requesting personal information, or attempting to influence others is prohibited.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>In case of violations, users should report them using the in-app reporting tools.</span>
                        </li>
                    </ul>
                </section>

                {/* Section 5 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-white text-[#C8102E] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
                        Developer Responsibilities
                    </h3>
                    <ul className="space-y-3 text-white/90">
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>The developer strives to provide a safe environment for all users.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Users are solely responsible for the content they create or share.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>The developer reserves the right to restrict access or suspend accounts that violate these rules.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Continued use of the application after updates to these terms implies acceptance of the new version.</span>
                        </li>
                    </ul>
                </section>

                {/* Section 6 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-white text-[#C8102E] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
                        Privacy Policy
                    </h3>
                    <ul className="space-y-3 text-white/90">
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Users' personal information is treated as <span className="font-semibold text-white">confidential and will be protected</span>.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Login details and phone numbers will not be shared without consent, except as required by law.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>The developer bears no responsibility for user negligence in protecting account data.</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-white mt-1">•</span>
                            <span>Games may contain advertisements; their type and placement may change without prior notice.</span>
                        </li>
                    </ul>
                </section>

                {/* Section 7 */}
                <section className="mb-8 bg-gradient-to-br from-[#C8102E]/90 via-[#E31E3C]/90 to-[#C8102E]/90 rounded-xl p-6 md:p-8 shadow-lg border border-red-300/40 backdrop-blur-sm">
                    <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                        <span className="bg-red-300 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">7</span>
                        Violations and Consequences
                    </h3>
                    <p className="mb-4 text-white/90 leading-relaxed">
                        Depending on the severity of the violation, the following actions may be taken:
                    </p>
                    <ul className="space-y-3 text-white/90">
                        <li className="flex items-start gap-3">
                            <span className="text-red-300 mt-1">⚠</span>
                            <span className="font-semibold">Content removal</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-red-300 mt-1">⚠</span>
                            <span className="font-semibold">Restricted access to certain features</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-red-300 mt-1">⚠</span>
                            <span className="font-semibold">Temporary or permanent account suspension</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-red-300 mt-1">⚠</span>
                            <span className="font-semibold">Legal action (if necessary)</span>
                        </li>
                    </ul>
                </section>

                {/* Footer Note */}
                <div className="mt-12 text-center text-[#2d1810] text-sm">
                    <p>By using our applications, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</p>
                    <p className="mt-2">For questions or concerns, please <a href="/contact" className="text-[#C8102E] hover:text-[#A00D25] underline font-semibold">contact us</a>.</p>
                </div>
            </div>
        </div>
    );
}

export default Terms;